import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Anchor, Group, Paper, Progress, Stack, Text } from '@mantine/core';
import { RadarChart } from '@mantine/charts';
import { useDevice } from '../../../hooks/useDevice';
import { useUserCompetencyMap } from '../../../hooks/useApi';
import { useUserStore } from '../../../store/userStore';

/**
 * Грейд одной карточкой: где человек сейчас, куда следующий шаг, сколько
 * компетенций до него и по каким дорожкам они распределены. Распределение
 * ролей (пончик) осталось в профиле: его заполняют руками, и у большинства
 * оно пустое.
 */
export const GradeCard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const device = useDevice();
  const user = useUserStore((s) => s.user);
  const { gradeDisplay } = useUserCompetencyMap();
  const { mode, currentGrade, nextGrade, progressPct, acquiredCount, totalCount, coverage } =
    gradeDisplay;

  // Имя ряда на радаре служит и подписью в подсказке, поэтому оно переведённое.
  const seriesLabel = t('competencies.percentageOfAcquisition');
  const radarData = coverage.map((c) => ({ swimlane: c.swimlane, [seriesLabel]: c.pct }));

  const title = currentGrade?.title || user?.grade?.title;

  return (
    <Paper withBorder radius="md" p="lg">
      <Stack gap="sm">
        <Text size="sm" fw={500}>
          {t('dashboard.grade.title')}
        </Text>

        {!title ? (
          <Text size="sm" c="dimmed">
            {t('dashboard.grade.none')}
          </Text>
        ) : (
          <>
            <Group gap={8} align="baseline" wrap="wrap">
              <Text fw={600} size="lg">
                {title}
              </Text>
              {nextGrade?.title && (
                <Text size="sm" c="dimmed">
                  {t('dashboard.grade.next', { name: nextGrade.title })}
                </Text>
              )}
            </Group>

            {mode !== 'noData' && (
              <>
                <Progress value={progressPct} color="teal" size="sm" radius="xl" />
                <Text size="xs" c="dimmed">
                  {mode === 'topGrade'
                    ? t('dashboard.grade.mastered', { acquired: acquiredCount, total: totalCount })
                    : t('dashboard.grade.toNext', { acquired: acquiredCount, total: totalCount })}
                </Text>

                {/* Радар отвечает на другой вопрос, чем полоса: не «сколько
                    осталось», а «где именно провал» — в технологиях, процессах
                    или работе с людьми. */}
                {radarData.length >= 3 && (
                  <RadarChart
                    h={device === 'mobile' ? 200 : 220}
                    withTooltip
                    data={radarData}
                    dataKey="swimlane"
                    series={[{ name: seriesLabel, color: 'teal', strokeColor: 'teal' }]}
                  />
                )}
              </>
            )}
          </>
        )}

        <Anchor
          component="button"
          type="button"
          size="sm"
          onClick={() => navigate('/grades')}
          style={{ alignSelf: 'flex-start' }}
        >
          {t('dashboard.grade.openMap')}
        </Anchor>
      </Stack>
    </Paper>
  );
};
